/**
 * localization-picker.js — <localization-picker>, the footer flag that opens "Choose your location",
 * and <country-select>, the flag beside the country list.
 *
 * The markup (snippets/localization-picker.liquid) opens and closes its <dialog> with the platform's
 * invoker commands (`commandfor` / `command`) and `closedby="any"`, so a current browser needs none
 * of this. The picker only fills in where a browser lacks them: a click on a command button runs
 * the command, and a click on the backdrop closes the dialog. Nothing is submitted from here; the
 * form posts as it is.
 *
 * <country-select> is rendered by snippets/localization-fields.liquid, so it serves the picker and
 * the first-visit prompt alike: when the chosen country changes, the flag beside the list follows.
 * Without JS the flag stays the current country's, which is still true until the form is saved.
 */
(() => {
  const invokers =
    typeof HTMLButtonElement !== 'undefined' && 'commandForElement' in HTMLButtonElement.prototype;
  const closedBy = typeof HTMLDialogElement !== 'undefined' && 'closedBy' in HTMLDialogElement.prototype;

  if (!customElements.get('localization-picker')) {
    class LocalizationPicker extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        if (!invokers) {
          this.onCommand = (event) => {
            const button = event.target.closest && event.target.closest('[commandfor]');
            if (!button || !this.contains(button)) return;
            const command = button.getAttribute('command');
            if (command === 'show-modal' && !this.dialog.open) this.dialog.showModal();
            if (command === 'close' && this.dialog.open) this.dialog.close();
          };
          this.addEventListener('click', this.onCommand);
        }

        if (!closedBy) {
          // A click on the backdrop lands on the dialog element itself, outside its content.
          this.onBackdrop = (event) => {
            if (event.target === this.dialog && this.dialog.open) this.dialog.close();
          };
          this.dialog.addEventListener('click', this.onBackdrop);
        }
      }

      disconnectedCallback() {
        if (this.onCommand) this.removeEventListener('click', this.onCommand);
        if (this.onBackdrop && this.dialog) this.dialog.removeEventListener('click', this.onBackdrop);
      }
    }

    customElements.define('localization-picker', LocalizationPicker);
  }

  if (!customElements.get('country-select')) {
    class CountrySelect extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('select');
        this.flag = this.querySelector('img');
        if (!this.select || !this.flag) return;
        this.onChange = () => {
          const value = this.select.value;
          const option = Array.prototype.find.call(this.select.options, (o) => o.value === value);
          const src = option && option.getAttribute('data-flag');
          if (src) this.flag.setAttribute('src', src);
          // The srcset carries the old country's flag and would win over src.
          this.flag.removeAttribute('srcset');
        };
        this.select.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        if (this.select && this.onChange) this.select.removeEventListener('change', this.onChange);
      }
    }

    customElements.define('country-select', CountrySelect);
  }
})();
